import Phaser from 'phaser';
import { COLORS, GAME_HEIGHT, GAME_WIDTH, ITEM_SIZE, BASKET_WIDTH, BASKET_HEIGHT } from '../config/constants';
import { trackAssetLoadFailure, trackAssetsLoaded } from '../systems/Telemetry';

/**
 * Generates procedural placeholder textures so the game is fully playable
 * before real art from ASSET.md exists. Every texture key here matches the
 * `key` naming used in ASSET.md / src/data/items.ts, so swapping in real
 * spritesheets later is a drop-in replacement — just add a real `this.load
 * .image(...)` / `this.load.atlas(...)` call above `createPlaceholders()`
 * and remove the matching generated-texture line.
 */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload(): void {
    this.createLoadingUI();
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      trackAssetLoadFailure(String(file.key), file.type);
    });

    // First real-art batch (Phase 1) — SVG placeholders in the ASSET.md style
    // guide, rasterized at load time. Keys match src/data/items.ts so they
    // slot in ahead of the matching procedural texture below.
    this.load.svg('item_pumpkin', 'assets/images/item_pumpkin.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_candy_corn', 'assets/images/item_candy_corn.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_bat', 'assets/images/item_bat.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_ornament', 'assets/images/item_ornament.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_candy_cane', 'assets/images/item_candy_cane.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_ghost', 'assets/images/item_ghost.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_present', 'assets/images/item_present.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_snowflake', 'assets/images/item_snowflake.svg', { width: ITEM_SIZE, height: ITEM_SIZE });
    this.load.svg('item_fusion_pumpkin_hat', 'assets/images/item_fusion_pumpkin_hat.svg', {
      width: ITEM_SIZE,
      height: ITEM_SIZE
    });
    this.load.svg('item_fusion_bat_ornament', 'assets/images/item_fusion_bat_ornament.svg', {
      width: ITEM_SIZE,
      height: ITEM_SIZE
    });

    // mascot_promo is loaded by BootScene, ahead of this loading screen.
    this.load.svg('mascot_idle', 'assets/images/mascot_idle.svg', { width: 128, height: 128 });
    this.load.svg('mascot_happy', 'assets/images/mascot_happy.svg', { width: 128, height: 128 });
    this.load.svg('mascot_oops', 'assets/images/mascot_oops.svg', { width: 128, height: 128 });

    this.load.svg('bg_halloweentown', 'assets/images/bg_halloweentown.svg', {
      width: GAME_WIDTH,
      height: GAME_HEIGHT
    });
    this.load.svg('bg_christmastown', 'assets/images/bg_christmastown.svg', {
      width: GAME_WIDTH,
      height: GAME_HEIGHT
    });

    this.load.svg('ui_panel', 'assets/images/ui_panel.svg', { width: 500, height: 320 });
    this.load.svg('ui_star_filled', 'assets/images/ui_star_filled.svg', { width: 48, height: 48 });
    this.load.svg('ui_star_empty', 'assets/images/ui_star_empty.svg', { width: 48, height: 48 });
    this.load.svg('node_locked', 'assets/images/node_locked.svg', { width: 80, height: 80 });
    this.load.svg('node_unlocked', 'assets/images/node_unlocked.svg', { width: 80, height: 80 });

    // Real audio (Phase 1) — generated by scripts/generate-audio.mjs per ASSET.md section 8.
    this.load.audio('sfx_catch_correct', 'assets/audio/sfx_catch_correct.wav');
    this.load.audio('sfx_catch_wrong', 'assets/audio/sfx_catch_wrong.wav');
    this.load.audio('sfx_item_miss', 'assets/audio/sfx_item_miss.wav');
    this.load.audio('sfx_combo_up', 'assets/audio/sfx_combo_up.wav');
    this.load.audio('sfx_level_complete', 'assets/audio/sfx_level_complete.wav');
    this.load.audio('sfx_level_fail', 'assets/audio/sfx_level_fail.wav');
    this.load.audio('sfx_button_tap', 'assets/audio/sfx_button_tap.wav');
    this.load.audio('sfx_star_award', 'assets/audio/sfx_star_award.wav');
    this.load.audio('music_theme_loop', 'assets/audio/music_theme_loop.wav');
    this.load.audio('music_menu_loop', 'assets/audio/music_menu_loop.wav');
  }

  create(): void {
    trackAssetsLoaded();
    this.createPlaceholderTextures();
    this.scene.start('MainMenu');
  }

  private createLoadingUI(): void {
    this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, 'mascot_promo').setScale(0.9);
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 10, 'Spooky Stockings', {
        fontFamily: 'Georgia, serif',
        fontSize: '36px',
        color: '#ffffff'
      })
      .setOrigin(0.5);

    const barWidth = 400;
    const barHeight = 24;
    const barX = GAME_WIDTH / 2 - barWidth / 2;
    const barY = GAME_HEIGHT / 2 + 40;

    const track = this.add.graphics();
    track.lineStyle(3, COLORS.fusionGold, 1);
    track.strokeRoundedRect(barX, barY, barWidth, barHeight, 8);

    const fill = this.add.graphics();
    this.load.on(Phaser.Loader.Events.PROGRESS, (value: number) => {
      fill.clear();
      fill.fillStyle(COLORS.fusionGold, 1);
      fill.fillRoundedRect(barX + 3, barY + 3, (barWidth - 6) * value, barHeight - 6, 5);
    });
  }

  private createPlaceholderTextures(): void {
    const g = this.make.graphics({ x: 0, y: 0 });

    // --- Gameplay items: all 10 now load real SVG art in preload() above. ---

    // --- Props ---
    this.drawBasket(g);
    this.drawPortal(g);
    this.drawParticle(g);

    // --- Backgrounds and UI (panel, star icons): all now load real SVG art in preload() above. ---

    g.destroy();
  }

  private drawBasket(g: Phaser.GameObjects.Graphics): void {
    g.clear();
    // One wide catcher with two local sorting compartments. The text/icon
    // labels are added by GameScene so they stay crisp at every scale.
    g.fillStyle(COLORS.halloweenOrange, 1);
    g.fillRoundedRect(0, 10, BASKET_WIDTH, BASKET_HEIGHT - 10, 10);
    g.fillStyle(COLORS.christmasBlue, 1);
    g.fillRoundedRect(BASKET_WIDTH / 2, 10, BASKET_WIDTH / 2, BASKET_HEIGHT - 10, 10);
    g.fillRect(BASKET_WIDTH / 2, 10, 12, BASKET_HEIGHT - 10);
    g.lineStyle(4, COLORS.outline, 1);
    g.strokeRoundedRect(0, 10, BASKET_WIDTH, BASKET_HEIGHT - 10, 10);
    g.lineBetween(BASKET_WIDTH / 2, 12, BASKET_WIDTH / 2, BASKET_HEIGHT - 2);
    g.lineStyle(4, COLORS.outline, 1);
    g.strokeEllipse(BASKET_WIDTH / 2, 8, BASKET_WIDTH - 20, 16);
    g.generateTexture('prop_basket', BASKET_WIDTH, BASKET_HEIGHT + 10);
  }

  private drawPortal(g: Phaser.GameObjects.Graphics): void {
    g.clear();
    const size = 160;
    const c = size / 2;
    g.fillStyle(COLORS.halloweenPurple, 0.9);
    g.fillCircle(c, c, c - 8);
    g.fillStyle(COLORS.christmasBlue, 0.7);
    g.fillCircle(c, c, c - 28);
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(c, c, c - 50);
    g.generateTexture('prop_portal', size, size);
  }

  private drawParticle(g: Phaser.GameObjects.Graphics): void {
    g.clear();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(8, 8, 8);
    g.generateTexture('fx_particle', 16, 16);
  }
}
